import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { setTheme } from '../../theme/theme';
import { Menu, X, ArrowRight } from 'lucide-react';
import krydaIcon from '../../assets/kryda-icon.png';
import './PublicHeader.css';

const toggleTheme = () => {
  const current = document.documentElement.getAttribute('data-theme');
  setTheme(current === 'dark' ? 'light' : 'dark');
};

const PublicHeader = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const theme = document.documentElement.getAttribute('data-theme') || 'dark';

  const scrollToSection = (id) => {
    setMobileMenuOpen(false);
    if (location.pathname !== '/') {
      navigate(`/#${id}`);
      return;
    }
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <header className="public-header">
      <div className="container header-content">
        {/* Logo Section */}
        <Link
          to="/"
          className="logo-section"
          aria-label="Vaultize Home"
        >
          <div className="logo-icon-wrapper">
            <img src={krydaIcon} alt="Vaultize" className="kryda-brand-logo-img" />
          </div>
          <div className="logo-titles">
            <span className="logo-text">Vaultize</span>
            <span className="logo-badge">Data Privacy Vault</span>
          </div>
        </Link>

        {/* Desktop Navigation */}
        <nav className="desktop-nav" aria-label="Main Navigation">
          <button
            type="button"
            className="nav-link"
            onClick={() => scrollToSection('simulator')}
          >
            Interactive Demo
          </button>
          <button
            type="button"
            className="nav-link"
            onClick={() => scrollToSection('features')}
          >
            Features
          </button>
          <button
            type="button"
            className="nav-link"
            onClick={() => scrollToSection('roi-calculator')}
          >
            Scope Calculator
          </button>
          <Link
            to="/plans"
            className={`nav-link ${location.pathname === '/plans' ? 'active' : ''}`}
          >
            Pricing
          </Link>
          <Link
            to="/docs"
            className={`nav-link ${location.pathname === '/docs' ? 'active' : ''}`}
          >
            Documentation
          </Link>
          <Link
            to="/contact"
            className={`nav-link ${location.pathname === '/contact' || location.pathname === '/book-demo' ? 'active' : ''}`}
          >
            Contact
          </Link>
        </nav>

        {/* Action CTAs */}
        <div className="header-actions">
          <Link
            to="/login"
            className={`btn-text ${location.pathname === '/login' ? 'active' : ''}`}
          >
            Sign in
          </Link>

          <button
            type="button"
            className="btn-header-demo"
            onClick={() => scrollToSection('book-demo-section')}
          >
            Book a Demo <ArrowRight size={14} aria-hidden="true" />
          </button>

          {/* Theme Toggle */}
          <button
            className={`theme-toggle ${theme}`}
            onClick={toggleTheme}
            aria-label="Toggle theme"
            title="Switch theme"
          >
            <span className="toggle-thumb" />
          </button>

          {/* Mobile Hamburger Toggle */}
          <button
            className="mobile-toggle-btn"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
            aria-expanded={mobileMenuOpen}
            aria-controls="mobile-drawer-menu"
          >
            {mobileMenuOpen ? <X size={22} aria-hidden="true" /> : <Menu size={22} aria-hidden="true" />}
          </button>
        </div>
      </div>

      {/* Mobile Drawer Menu */}
      {mobileMenuOpen && (
        <div className="mobile-drawer" id="mobile-drawer-menu">
          <button
            type="button"
            className="mobile-nav-link"
            onClick={() => scrollToSection('simulator')}
          >
            Interactive Demo
          </button>
          <button
            type="button"
            className="mobile-nav-link"
            onClick={() => scrollToSection('features')}
          >
            Features
          </button>
          <button
            type="button"
            className="mobile-nav-link"
            onClick={() => scrollToSection('roi-calculator')}
          >
            Scope Calculator
          </button>
          <Link
            to="/plans"
            className="mobile-nav-link"
            onClick={() => setMobileMenuOpen(false)}
          >
            Pricing
          </Link>
          <Link
            to="/docs"
            className="mobile-nav-link"
            onClick={() => setMobileMenuOpen(false)}
          >
            Documentation
          </Link>
          <Link
            to="/contact"
            className="mobile-nav-link"
            onClick={() => setMobileMenuOpen(false)}
          >
            Contact Us
          </Link>
          <div className="mobile-drawer-actions">
            <Link
              to="/login"
              className="btn btn-secondary w-full"
              onClick={() => setMobileMenuOpen(false)}
            >
              Sign in
            </Link>
            <button
              type="button"
              className="btn-header-demo w-full"
              onClick={() => scrollToSection('book-demo-section')}
            >
              Book a Demo
            </button>
          </div>
        </div>
      )}
    </header>
  );
};

export default PublicHeader;
